import { useEffect, useRef } from 'react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useCookieConsent } from '@/components/CookieConsent';
import { supabase } from '@/integrations/supabase/client';
import { cleanupAdsense, resumeAdsenseRequests } from '@/lib/adsenseCleanup';
import { updateConsentMode } from '@/lib/consentManager';

const ADSENSE_CLIENT_ID = 'ca-pub-8146717966703682';
const DEBUG_ADS = import.meta.env.DEV;

/**
 * AdSenseLoader dynamically injects the AdSense script ONLY after the user
 * has interacted with the cookie consent banner. This prevents Google from
 * showing its own Funding Choices consent popup.
 *
 * Flow:
 * - No consent yet → nothing loads, no Google scripts/meta in DOM
 * - User accepts ads → load personalized ads
 * - User rejects ads (but consented) → load non-personalized ads
 * - Paid user → never load AdSense
 */
export const AdSenseLoader = () => {
  const { effectiveTier, loading: subscriptionLoading, adsAllowed } = useSubscription();
  const { hasConsented, preferences } = useCookieConsent();
  const scriptLoaded = useRef(false);
  const previousTierRef = useRef<string | null>(null);

  // Track tier changes and cleanup when user becomes paid
  useEffect(() => {
    if (subscriptionLoading) return;

    const currentTier = effectiveTier;
    const previousTier = previousTierRef.current;

    if (previousTier === 'free' && (currentTier === 'pro' || currentTier === 'chef_master')) {
      if (DEBUG_ADS) console.log('[AdSenseLoader] Tier changed from free to paid - cleaning up AdSense');
      cleanupAdsense();
      scriptLoaded.current = false;
    }

    // Returning to free (e.g. subscription ended) — allow script again
    if (
      previousTier &&
      previousTier !== 'free' &&
      currentTier === 'free'
    ) {
      scriptLoaded.current = false;
      resumeAdsenseRequests();
      if (DEBUG_ADS) console.log('[AdSenseLoader] Returned to free tier - ready to reload AdSense');
    }

    previousTierRef.current = currentTier;
  }, [effectiveTier, subscriptionLoading]);

  // Listen for auth changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === 'SIGNED_IN') {
        if (DEBUG_ADS) console.log('[AdSenseLoader] User signed in - waiting for subscription check');
      } else if (event === 'SIGNED_OUT') {
        scriptLoaded.current = false;
        previousTierRef.current = null;
        resumeAdsenseRequests();
        if (DEBUG_ADS) console.log('[AdSenseLoader] User signed out - reset state + resume ads');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Main effect: ONLY load AdSense AFTER user has interacted with consent banner
  useEffect(() => {
    if (subscriptionLoading) {
      if (DEBUG_ADS) console.log('[AdSenseLoader] Waiting for subscription...');
      return;
    }

    // CRITICAL: Don't load for paid users
    if (effectiveTier !== 'free') {
      if (DEBUG_ADS) {
        console.log('[AdSenseLoader] Skipping - paid tier:', effectiveTier);
      }
      if (scriptLoaded.current || document.querySelector('script[src*="pagead2.googlesyndication.com"]')) {
        cleanupAdsense();
        scriptLoaded.current = false;
      }
      return;
    }

    if (!adsAllowed) {
      if (DEBUG_ADS) console.log('[AdSenseLoader] Skipping - adsAllowed is false');
      return;
    }

    // CRITICAL: Do NOT load AdSense until the user has interacted with the consent banner.
    if (!hasConsented) {
      if (DEBUG_ADS) console.log('[AdSenseLoader] Waiting for user to interact with consent banner...');
      return;
    }

    const useNonPersonalized = !preferences.advertising;
    loadScript(useNonPersonalized, preferences.analytics, preferences.advertising);
  }, [effectiveTier, subscriptionLoading, adsAllowed, hasConsented, preferences.advertising, preferences.analytics]);

  const loadScript = (
    useNonPersonalized: boolean,
    analyticsConsent: boolean,
    advertisingConsent: boolean,
  ) => {
    // Always sync consent to the real preference pair (never force analytics=false)
    updateConsentMode(analyticsConsent, advertisingConsent);
    resumeAdsenseRequests();

    const existing = document.querySelector(
      `script[src*="pagead2.googlesyndication.com"]`,
    ) as HTMLScriptElement | null;

    if (existing || scriptLoaded.current) {
      scriptLoaded.current = true;
      window.__adsenseReady = true;
      window.__adsenseScriptLoaded = true;
      if (useNonPersonalized) {
        window.adsbygoogle = window.adsbygoogle || [];
        (window.adsbygoogle as { requestNonPersonalizedAds?: number }).requestNonPersonalizedAds = 1;
      }
      if (DEBUG_ADS) console.log('[AdSenseLoader] Script already present — resumed requests');
      return;
    }

    // Dynamically inject the AdSense account meta tag
    if (!document.querySelector('meta[name="google-adsense-account"]')) {
      const meta = document.createElement('meta');
      meta.name = 'google-adsense-account';
      meta.content = ADSENSE_CLIENT_ID;
      document.head.appendChild(meta);
      if (DEBUG_ADS) console.log('[AdSenseLoader] Injected google-adsense-account meta tag');
    }

    if (useNonPersonalized) {
      window.adsbygoogle = window.adsbygoogle || [];
      (window.adsbygoogle as { requestNonPersonalizedAds?: number }).requestNonPersonalizedAds = 1;
      if (DEBUG_ADS) console.log('[AdSenseLoader] Configured for non-personalized ads');
    }

    const script = document.createElement('script');
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.async = true;
    script.crossOrigin = 'anonymous';

    script.onload = () => {
      if (DEBUG_ADS) console.log('[AdSenseLoader] Script loaded successfully');
      scriptLoaded.current = true;
      window.__adsenseReady = true;
      window.__adsenseScriptLoaded = true;
      resumeAdsenseRequests();
    };

    script.onerror = () => {
      if (DEBUG_ADS) console.log('[AdSenseLoader] Script failed to load (ad blocker?)');
      window.__adsenseReady = false;
      window.__adsenseScriptLoaded = false;
      scriptLoaded.current = false;
    };

    document.head.appendChild(script);
    if (DEBUG_ADS) {
      console.log(
        '[AdSenseLoader] Script injected -',
        useNonPersonalized ? 'non-personalized' : 'personalized',
      );
    }
  };

  return null;
};
